import { useCallback } from "react";
import { LoadingIndicator } from "@jsq/ds/core";
import { FormSelect, useFormContext } from "@jsq/ds/forms";
import { ComplianceCaseTypeEnum as CaseTypeEnum } from "~/graphql/supergraphEnums";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphTypes";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useIsFatcaCrsEnabledForCase } from "~/pages/Compliance/flags";
import {
  DOCUMENT_TYPE_MAPPING,
  JOINT_PROFILE_ALLOWED_DOCUMENT_TYPES,
  JOINT_PROFILE_RESTRICTED_DOC_TYPE_TOOLTIP,
  descriptionOnlyDocumentTypes,
  useDocumentTypesOptions,
} from "../constants";
import { useChangeAndResetDocumentData } from "../hooks/useChangeAndResetDocumentData";
import { ResetDocumentDataWarningModal } from "../ResetDocumentDataWarningModal";
import { useResetWarningModal } from "../ResetDocumentDataWarningModal/useResetWarningModal";
import { FormValues } from "../types";
import { isBaseDocumentType, isTaxDocumentType } from "../utils";
import AddressVerificationFormValues from "./AddressVerificationFormValues";
import AMLRelianceLetterDocFormValues from "./AMLRelianceLetterDocFormValues";
import AnnualReportFormValues from "./AnnualReportFormValues";
import AuthorizedSignatoryFormValues from "./AuthorizedSignatoryFormValues";
import ConfirmationOfClassBeneficiariesFormValues from "./ConfirmationOfClassBeneficiariesFormValues";
import EscalationWaiverFormValues from "./EscalationWaiverFormValues";
import EvidenceOfFormationFormValues from "./EvidenceOfFormationFormValues";
import EvidenceOfRegulationFormValues from "./EvidenceOfRegulationFormValues";
import EvidenceOnPublicExchangeFormValues from "./EvidenceOnPublicExchangeFormValues";
import Form990FormValues from "./Form990FormValues";
import FormationDocFormValues from "./FormationDocFormValues";
import IdDocFormValues from "./IdDocFormValues";
import KYCChecklistFormValues from "./KYCChecklistFormValues";
import OrganizationalDocFormValues from "./OrganizationalDocFormValues";
import PayeeMismatchFormValues from "./PayeeMismatchFormValues";
import RegisterOfDirectorsFormValues from "./RegisterOfDirectorsFormValues";
import RegisterOfMembersFormValues from "./RegisterOfMembersFormValues";
import DescriptionOnlyFormValues from "./ReusableFormValues/DescriptionOnlyFormValues";
import DocumentStatusRadioBar from "./ReusableFormValues/DocumentStatusRadioBar";
import ScreeningResultsFormvalues from "./ScreeningResultsFormValues";
import SelfCertificationFormValues from "./SelfCertificationFormValues";
import ShareRegisterFormValues from "./ShareRegisterFormValues";
import * as styles from "./styles_vanilla.css";
import TrustDeedFormValues from "./TrustDeedFormValues";
import WSeriesTaxDocFormValues from "./WSeriesTaxDocFormValues";

export type CaseInfo = {
  type: ComplianceCaseTypeEnum;
  description?: string;
};

export type Props = {
  readonly: boolean;
  isExtracting?: boolean;
  caseInfo?: CaseInfo;
  documentName?: string;
  documentId: number;
  isProfileSelected?: boolean;
  shouldApplyJointRestrictions?: boolean;
};

export const DocumentDetailsFormValues = ({
  readonly,
  isExtracting,
  caseInfo,
  documentName = "",
  documentId,
  isProfileSelected = true,
  shouldApplyJointRestrictions = false,
}: Props) => {
  const { values, setFieldValue } = useFormContext<FormValues>();
  const { caseId, caseType, fatcaCrsEnabled } = useCurrentCaseContext();
  const isFatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    caseType as CaseTypeEnum | undefined,
    fatcaCrsEnabled,
  );
  const documentTypesOptions = useDocumentTypesOptions(isFatcaCrsEnabled);

  const { changeAndReset } = useChangeAndResetDocumentData({
    caseId,
    documentId,
  });

  const shouldShowModal = useCallback(() => {
    return isTaxDocumentType(values.documentType);
  }, [values.documentType]);

  const getDocumentTypeReadableName = useCallback(
    (typeValue: string) => {
      const option = documentTypesOptions.find((opt) => opt.value === typeValue);
      return option?.label ?? typeValue;
    },
    [documentTypesOptions],
  );

  const handleDocumentTypeChangeConfirm = useCallback(
    async (newValue: string) => {
      return changeAndReset({
        resetInfo: {
          profileId: Number(values.relatedProfile),
          documentPurpose: isBaseDocumentType(newValue) ? newValue : null,
          documentStatus: null,
          subType: null,
        },
      });
    },
    [changeAndReset, values.relatedProfile],
  );

  const handleDocumentTypeDirectChange = useCallback(
    (newValue: string) => {
      void setFieldValue("documentType", newValue);
      void setFieldValue("secondaryDocumentType", "");
      void setFieldValue("documentStatus", "");
      void setFieldValue("isCertifiedDocument", false);
      void setFieldValue("description", "");
    },
    [setFieldValue],
  );

  const handleDocumentTypeChangeCancel = useCallback(
    (previousValue: string) => {
      void setFieldValue("documentType", previousValue);
    },
    [setFieldValue],
  );

  const {
    showModal: showResetModal,
    pendingChange,
    isLoading: isModalLoading,
    handleFieldChange: handleDocTypeFieldChange,
    handleConfirm: handleResetConfirm,
    handleCancel: handleResetCancel,
  } = useResetWarningModal({
    onConfirm: handleDocumentTypeChangeConfirm,
    onCancel: handleDocumentTypeChangeCancel,
    onDirectChange: handleDocumentTypeDirectChange,
    getDocumentName: () => documentName,
    getCurrentValue: () => values.documentType ?? "",
    getReadableValue: getDocumentTypeReadableName,
    changeType: "documentType",
    shouldShowModal,
  });

  const handleDocumentTypeSelectChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const newValue = String(value);
      // Disabled options stay reachable via keyboard, and FormSelect commits the
      // value before this handler runs, so explicitly revert restricted picks.
      if (shouldApplyJointRestrictions && !JOINT_PROFILE_ALLOWED_DOCUMENT_TYPES.has(newValue)) {
        void setFieldValue("documentType", values.documentType ?? "");
        return;
      }
      if (!isFatcaCrsEnabled) {
        return;
      }
      if (values.documentType) {
        handleDocTypeFieldChange(newValue);
      } else {
        void setFieldValue("documentType", newValue);
        void setFieldValue("secondaryDocumentType", "");
        void setFieldValue("documentStatus", "");
        void setFieldValue("isCertifiedDocument", false);
        void setFieldValue("description", "");
      }
    },
    [
      isFatcaCrsEnabled,
      shouldApplyJointRestrictions,
      values.documentType,
      handleDocTypeFieldChange,
      setFieldValue,
    ],
  );

  if (isExtracting) {
    return <LoadingIndicator variant="primary" />;
  }

  return (
    <>
      <FormSelect
        full
        className={styles.documentTypeSelect}
        label="Document type"
        placeholder="Select a document type"
        name="documentType"
        disabled={readonly || !isProfileSelected}
        handleChange={handleDocumentTypeSelectChange}
        onSelect={
          isFatcaCrsEnabled
            ? undefined
            : () => {
                void setFieldValue("secondaryDocumentType", "");
                void setFieldValue("documentStatus", "");
                void setFieldValue("isCertifiedDocument", false);
                void setFieldValue("description", "");
              }
        }
      >
        <FormSelect.Section>
          {documentTypesOptions.map((type) => {
            const isDisabledForJoint =
              shouldApplyJointRestrictions &&
              !JOINT_PROFILE_ALLOWED_DOCUMENT_TYPES.has(type.value);
            return (
              <FormSelect.Option
                key={type.value}
                value={type.value}
                disabled={isDisabledForJoint}
              >
                {isDisabledForJoint ? (
                  <ExtendedInfoTooltip message={JOINT_PROFILE_RESTRICTED_DOC_TYPE_TOOLTIP}>
                    <span className={styles.disabledOptionLabel}>{type.label}</span>
                  </ExtendedInfoTooltip>
                ) : (
                  type.label
                )}
              </FormSelect.Option>
            );
          })}
        </FormSelect.Section>
      </FormSelect>
      {values.documentType === DOCUMENT_TYPE_MAPPING.addressVerification.enumValue && (
        <AddressVerificationFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.amlRelianceLetter.enumValue && (
        <AMLRelianceLetterDocFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.annualReport.enumValue && (
        <AnnualReportFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.authorizedSignatoryList.enumValue && (
        <AuthorizedSignatoryFormValues readonly={readonly} documentName={documentName} />
      )}
      {values.documentType ===
        DOCUMENT_TYPE_MAPPING.confirmationOfTheClassOfBeneficiaries.enumValue && (
        <ConfirmationOfClassBeneficiariesFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.evidenceOfGov.enumValue && (
        <EvidenceOfFormationFormValues readonly={readonly} />
      )}
      {values.documentType ===
        DOCUMENT_TYPE_MAPPING.evidenceOfListingOnPublicExchange.enumValue && (
        <EvidenceOnPublicExchangeFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.evidenceOfRegulation.enumValue && (
        <EvidenceOfRegulationFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.form990.enumValue && (
        <Form990FormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.formation_document.enumValue && (
        <FormationDocFormValues readonly={readonly} documentName={documentName} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.id.enumValue && (
        <IdDocFormValues readonly={readonly} documentName={documentName} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.kycChecklist.enumValue && (
        <KYCChecklistFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.organizational.enumValue && (
        <OrganizationalDocFormValues readonly={readonly} documentName={documentName} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.payeeMismatch.enumValue && (
        <PayeeMismatchFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.registerOfDirectors.enumValue && (
        <RegisterOfDirectorsFormValues readonly={readonly} documentName={documentName} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.registerOfMembers.enumValue && (
        <RegisterOfMembersFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.screeningResults.enumValue && (
        <ScreeningResultsFormvalues readonly={readonly} />
      )}
      {isFatcaCrsEnabled &&
        values.documentType === DOCUMENT_TYPE_MAPPING.selfCertification.enumValue && (
          <SelfCertificationFormValues readonly={readonly} documentName={documentName} />
        )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.shareRegister.enumValue && (
        <ShareRegisterFormValues readonly={readonly} documentName={documentName} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.trustDeed.enumValue && (
        <TrustDeedFormValues readonly={readonly} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue && (
        <WSeriesTaxDocFormValues readonly={readonly} documentName={documentName} />
      )}
      {values.documentType === DOCUMENT_TYPE_MAPPING.waiver.enumValue && (
        <div className={styles.descriptionWrapper}>
          {caseInfo?.type === CaseTypeEnum.Escalation && caseInfo?.description ? (
            <EscalationWaiverFormValues description={caseInfo.description} />
          ) : (
            <DescriptionOnlyFormValues isRequired readonly={readonly} />
          )}
        </div>
      )}
      {descriptionOnlyDocumentTypes[values.documentType ?? ""] && (
        <div className={styles.descriptionWrapper}>
          <DescriptionOnlyFormValues
            isRequired={descriptionOnlyDocumentTypes[values.documentType ?? ""]?.required}
            readonly={readonly}
          />
        </div>
      )}
      {values.documentType &&
        !(
          isFatcaCrsEnabled &&
          (values.documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue ||
            values.documentType === DOCUMENT_TYPE_MAPPING.selfCertification.enumValue)
        ) && (
          <div className={styles.statusRadioWrapper}>
            <DocumentStatusRadioBar readonly={readonly} />
          </div>
        )}
      <ResetDocumentDataWarningModal
        show={showResetModal}
        onHide={handleResetCancel}
        onConfirm={handleResetConfirm}
        changeDetails={pendingChange}
        isLoading={isModalLoading}
      />
    </>
  );
};
